/**
 * Matomo events (change of 20 Oct 2026, 1.6.0; event list in docs/handoff/customisation.md › Matomo). The ONLY
 * module that touches window._paq: the tracker itself is SEOPress's snippet, whose settings are never changed here.
 *
 * Display-only: an event forwards a server code or a guest action (a fixed word below), never a fact derived here.
 * Nothing personal: names pass the safety filter (no @, booking reference, Beds24 ID, token, or a value registered
 * with forbid() — the promo code typed, the booking reference). Values are whole numbers.
 * Never breaks the booking: every call is wrapped, returns at once, awaits nothing; no tracker = nothing happens.
 *
 * Events wait in memory until POST /start says whether the visitor is internal (test mode or a logged-in
 * administrator, computed per request: never in the page HTML), then go out as « Réservation » or « Réservation
 * test »; if /start fails they go out as « Réservation ». Caps per page load from the page payload (« Protection »
 * settings): maxEvents, maxJsErrors; 0 = nothing sent; no payload = nothing sent.
 *
 * « Suivi Matomo » (1.8.0, page payload `level`, a « Protection » setting): off = nothing; essential = ESSENTIAL and
 * ERRORS only; detailed (default) = every event. An event outside the level is never pushed (nor counted). Detail
 * events go out under « Réservation · détail » (« Réservation test · détail »). Values only on VALUED.
 */

const CATEGORY = 'Réservation';
const CATEGORY_TEST = 'Réservation test';
const DETAIL = ' · détail';
const LEVELS = ['off', 'essential', 'detailed'];
/** The funnel (level « Essentiel »): category « Réservation ». */
const ESSENTIAL = new Set(['recherche', 'tarif-choisi', 'details', 'paiement', 'confirmee']);
/** Errors: sent at « Essentiel » and « Détaillé », category « Réservation ». */
const ERRORS = new Set(['chargement-echec', 'paiement-refuse', 'erreur', 'erreur-js']);
/** The only events with a value: a count of rooms. */
const VALUED = new Set(['recherche', 'chambres', 'confirmee']);
const DEBUG_KEY = 'pap.analytics.debug'; // localStorage '1': each event also printed with console.debug

/** Our modules' URLs start here (assets/src or assets/dist): an uncaught error elsewhere is not ours. */
const OURS = (() => {
	try {
		const url = String(import.meta.url);
		const at = url.indexOf('/assets/');
		return at >= 0 ? url.slice(0, at + 8) : null;
	} catch (_e) {
		return null;
	}
})();

/** Safety filter on names: these patterns only (plain numbers — offer numbers, counts — always pass). */
const BLOCKED = [
	/@/, // an e-mail
	/\bPAP-[A-Z0-9]+/i, // a booking reference
	/\d{5,}/, // a Beds24 ID (room, booking, property: 6 digits or more)
	/(?=[A-Za-z0-9_-]*\d)(?=[A-Za-z0-9_-]*[a-z])[A-Za-z0-9_-]{20,}/ // a token (long, with digits and lower case)
];

const st = {
	caps: null, // {maxEvents, maxJsErrors, level}
	level: 'detailed',
	category: null, // null until /start answers
	held: [],
	sent: 0,
	jsErrors: 0,
	forbidden: new Set(),
	marked: new WeakSet(),
	listening: false
};

function debug(...args) {
	try {
		if (window.localStorage.getItem(DEBUG_KEY) === '1') {
			console.debug('[papounan analytics]', ...args);
		}
	} catch (_e) {
		// no storage: no debug
	}
}

function cap(key) {
	const n = st.caps?.[key];
	return Number.isInteger(n) && n > 0 ? n : 0;
}

/** True when a name may be sent (safety filter). */
export function allowed(name) {
	if (name === undefined) {
		return true;
	}
	const text = String(name);
	if (BLOCKED.some((re) => re.test(text))) {
		return false;
	}
	// A registered value: the whole name or one of its parts; inside a longer name when it is long enough to be
	// unmistakable (a short code such as « OK » must not drop `recherche ok`).
	const upper = text.toUpperCase();
	const parts = upper.split(/[-:]/);
	for (const value of st.forbidden) {
		if (upper === value || parts.includes(value) || (value.length >= 6 && upper.includes(value))) {
			return false;
		}
	}
	return true;
}

/** Detail events (not in ESSENTIAL or ERRORS): only at « Détaillé », in their own category. */
const isDetail = (action) => !ESSENTIAL.has(action) && !ERRORS.has(action);

function push(event) {
	try {
		if (st.sent >= cap('maxEvents')) {
			return;
		}
		st.sent++;
		const [action, name, value] = event;
		const entry = ['trackEvent', st.category + (isDetail(action) ? DETAIL : ''), action];
		if (name !== undefined || value !== undefined) {
			entry.push(name === undefined ? '' : name);
		}
		if (value !== undefined) {
			entry.push(value);
		}
		window._paq = window._paq || [];
		window._paq.push(entry);
		debug(entry);
	} catch (_e) {
		// tracker broken or blocked: the booking goes on
	}
}

/**
 * One event. name: a server code or a fixed word (or undefined); value: a whole number (or undefined).
 */
export function track(action, name, value) {
	try {
		if (!st.caps || cap('maxEvents') === 0 || st.level === 'off' || (st.level === 'essential' && isDetail(action))) {
			return;
		}
		const n = name === undefined || name === null || name === '' ? undefined : String(name);
		const v = VALUED.has(action) && Number.isInteger(value) ? value : undefined;
		if (!allowed(n)) {
			debug('dropped by the safety filter', action);
			return;
		}
		if (st.category === null) {
			if (st.held.length < cap('maxEvents')) {
				st.held.push([action, n, v]);
			}
			return;
		}
		push([action, n, v]);
	} catch (_e) {
		// never breaks the booking
	}
}

/** A value never to send (promo code typed, booking reference, access token): names containing it are dropped. */
export function forbid(value) {
	try {
		const text = String(value ?? '').trim().toUpperCase();
		if (text) {
			st.forbidden.add(text);
		}
	} catch (_e) {
		// nothing to remember
	}
}

/** The server's error code of a response, else GENERIC. */
const codeOf = (r) => r?.body?.error?.code || 'GENERIC';

/**
 * A response that already has its own event (chargement-echec, paiement-refuse): `erreur` skips it. Returns the code.
 */
export function mark(response) {
	try {
		if (response && typeof response === 'object') {
			st.marked.add(response);
		}
	} catch (_e) {
		// not an object
	}
	return codeOf(response);
}

/** errorFor(): the catch-all `erreur`, once per response, never for one marked above. */
export function trackError(response) {
	try {
		if (response && typeof response === 'object') {
			if (st.marked.has(response)) {
				return;
			}
			st.marked.add(response);
		}
		track('erreur', codeOf(response));
	} catch (_e) {
		// never breaks the booking
	}
}

/** `chargement-echec`: the page load answer was not 200. */
export function trackLoadFailure(response) {
	const status = response?.status;
	const code = response?.body?.error?.code || (status ? 'HTTP_' + status : 'GENERIC');
	mark(response);
	track('chargement-echec', code);
}

/** `api`: status 0, 429 or 5xx, from api.js call(); path without IDs or query. */
export function trackApi(path, response) {
	const status = response?.status;
	if (status === 0 || status === 429 || status >= 500) {
		track('api', String(path).split('?')[0] + ':' + (status === 0 ? '0' : response?.body?.error?.code || status));
	}
}

/**
 * The /start answer: internal visitor (test cookie or administrator) → « Réservation test »; held events go out.
 * @param {boolean} internal
 */
export function context(internal) {
	try {
		if (st.category !== null) {
			return;
		}
		st.category = internal === true ? CATEGORY_TEST : CATEGORY;
		const held = st.held;
		st.held = [];
		for (const event of held) {
			push(event);
		}
	} catch (_e) {
		// never breaks the booking
	}
}

/** A short hash (FNV-1a, synchronous): the booking reference itself is never stored. */
function hash(text) {
	let h = 0x811c9dc5;
	for (let i = 0; i < text.length; i++) {
		h ^= text.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return (h >>> 0).toString(36);
}

const ONCE_KEY = 'pap.confirmee.';

/**
 * `confirmee`, once per booking in this tab (sessionStorage, keyed by a hash of the reference); storage off → sent
 * anyway (a duplicate is better than a missing confirmation).
 */
export function trackConfirmed(ref, method, rooms) {
	try {
		const key = ONCE_KEY + hash(String(ref || ''));
		let seen = false;
		try {
			seen = window.sessionStorage.getItem(key) === '1';
			window.sessionStorage.setItem(key, '1');
		} catch (_e) {
			seen = false;
		}
		if (!seen) {
			track('confirmee', method, rooms);
		}
	} catch (_e) {
		// never breaks the booking
	}
}

/** `erreur-js` text: 100 characters, without e-mails, URL queries or digits. */
export function cleanMessage(message) {
	return String(message ?? '')
		.replace(/\S+@\S+/g, '')
		.replace(/\?\S*/g, '')
		.replace(/\d+/g, '')
		.replace(/\s+/g, ' ')
		.trim()
		.slice(0, 100);
}

function ours(...where) {
	return Boolean(OURS) && where.some((w) => typeof w === 'string' && w.includes(OURS));
}

function jsError(message) {
	try {
		if (st.jsErrors >= cap('maxJsErrors')) {
			return;
		}
		st.jsErrors++;
		track('erreur-js', cleanMessage(message) || 'GENERIC');
	} catch (_e) {
		// never breaks the booking
	}
}

/**
 * Page start (page.js): the caps and level from the page payload, the uncaught-error listeners.
 * No `page` event since 1.9.5 (change of 22 Oct 2026): Matomo already records the page view of the same URL.
 * @param {{caps: ?{maxEvents: number, maxJsErrors: number, level?: string}}} options
 */
export function initAnalytics({ caps }) {
	try {
		st.caps = caps && typeof caps === 'object' ? caps : null;
		st.level = LEVELS.includes(st.caps?.level) ? st.caps.level : 'detailed'; // a payload without level: the default
		if (st.listening || !st.caps || typeof window.addEventListener !== 'function') {
			return;
		}
		st.listening = true;
		window.addEventListener('error', (e) => {
			if (ours(e?.filename, e?.error?.stack)) {
				jsError(e.message);
			}
		});
		window.addEventListener('unhandledrejection', (e) => {
			const reason = e?.reason;
			if (ours(reason?.stack)) {
				jsError(reason?.message ?? reason);
			}
		});
	} catch (_e) {
		// never breaks the booking
	}
}

/** Tests only: a fresh page load. */
export function resetForTests() {
	Object.assign(st, { caps: null, level: 'detailed', category: null, held: [], sent: 0, jsErrors: 0, forbidden: new Set(), marked: new WeakSet(), listening: false });
}
